import { ArrowUpRight, BarChart3, Brain, Boxes, Cpu, LayoutDashboard } from "lucide-react";

const SOLS = [
  { name: "AI Over BI", icon: BarChart3 },
  { name: "Enterprise Brain", icon: Brain },
  { name: "Data Products", icon: Boxes },
  { name: "AI Models", icon: Cpu },
  { name: "Dashboards & Reporting", icon: LayoutDashboard },
];
const ACTIVE = 1; // "Enterprise Brain"

/* Each option is a function (item, isActive) => className bundle via render */

/* A · Solid espresso pill */
const A = () => (
  <Rail render={(x, on, i) => (
    <button key={x.name} className={`text-left rounded-lg px-4 py-3 flex items-center gap-3 transition-colors duration-300 ${on ? "bg-[#211307]" : "hover:bg-white/50"}`}>
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${on ? "bg-white/10 text-[#E8AA68]" : "bg-[#3E2818]/5 text-[#3E2818]/45"}`}><x.icon size={15} strokeWidth={1.9} /></span>
      <span className={`text-[13.5px] font-semibold ${on ? "text-[#F7F5F0]" : "text-[#3E2818]/55"}`}>{x.name}</span>
      <ArrowUpRight size={15} className={`ml-auto ${on ? "text-[#E8AA68] opacity-100" : "opacity-0"}`} />
    </button>
  )} />
);

/* B · Caramel filled */
const B = () => (
  <Rail render={(x, on) => (
    <button key={x.name} className={`text-left rounded-lg px-4 py-3 flex items-center gap-3 transition-colors duration-300 ${on ? "bg-[#8A5730]" : "hover:bg-white/50"}`}>
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${on ? "bg-white/20 text-white" : "bg-[#3E2818]/5 text-[#3E2818]/45"}`}><x.icon size={15} strokeWidth={1.9} /></span>
      <span className={`text-[13.5px] font-semibold ${on ? "text-white" : "text-[#3E2818]/55"}`}>{x.name}</span>
      <ArrowUpRight size={15} className={`ml-auto ${on ? "text-white opacity-100" : "opacity-0"}`} />
    </button>
  )} />
);

/* C · Minimal — no fill, gold bar + bold text */
const C = () => (
  <Rail render={(x, on) => (
    <button key={x.name} className={`text-left px-4 py-3 flex items-center gap-3 border-l-2 transition-colors duration-300 ${on ? "border-[#EDA13A]" : "border-transparent hover:border-[#3E2818]/15"}`}>
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${on ? "bg-[#EDA13A]/15 text-[#B87A32]" : "bg-transparent text-[#3E2818]/40"}`}><x.icon size={15} strokeWidth={1.9} /></span>
      <span className={`text-[13.5px] font-semibold ${on ? "text-[#3E2818]" : "text-[#3E2818]/50"}`}>{x.name}</span>
    </button>
  )} />
);

/* D · Cream-gold card + gold ring */
const D = () => (
  <Rail render={(x, on) => (
    <button key={x.name} className={`text-left rounded-lg px-4 py-3 flex items-center gap-3 transition-colors duration-300 ${on ? "bg-[#FBF2E1] ring-1 ring-inset ring-[#E8AA68]" : "hover:bg-white/50"}`}>
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${on ? "bg-[#E8AA68]/30 text-[#8A5730]" : "bg-[#3E2818]/5 text-[#3E2818]/45"}`}><x.icon size={15} strokeWidth={1.9} /></span>
      <span className={`text-[13.5px] font-semibold ${on ? "text-[#3E2818]" : "text-[#3E2818]/55"}`}>{x.name}</span>
      <ArrowUpRight size={15} className={`ml-auto text-[#8A5730] ${on ? "opacity-100" : "opacity-0"}`} />
    </button>
  )} />
);

/* E · Filled icon chip + text color, subtle bg */
const E = () => (
  <Rail render={(x, on) => (
    <button key={x.name} className={`text-left rounded-lg px-4 py-3 flex items-center gap-3 transition-colors duration-300 ${on ? "bg-white/70" : "hover:bg-white/40"}`}>
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-300 ${on ? "bg-[#8A5730] text-white" : "bg-[#3E2818]/5 text-[#3E2818]/45"}`}><x.icon size={15} strokeWidth={1.9} /></span>
      <span className={`text-[13.5px] font-semibold ${on ? "text-[#8A5730]" : "text-[#3E2818]/55"}`}>{x.name}</span>
    </button>
  )} />
);

/* F · White elevated card, no left bar, gold dot marker */
const F = () => (
  <Rail render={(x, on) => (
    <button key={x.name} className={`text-left rounded-xl px-4 py-3 flex items-center gap-3 transition-all duration-300 ${on ? "bg-white shadow-[0_16px_38px_-24px_rgba(62,40,24,0.5)]" : "hover:bg-white/50"}`}>
      <span className={`w-2 h-2 rounded-full shrink-0 ${on ? "bg-[#EDA13A]" : "bg-[#3E2818]/20"}`} />
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${on ? "bg-[#8A5730]/12 text-[#8A5730]" : "bg-[#3E2818]/5 text-[#3E2818]/45"}`}><x.icon size={15} strokeWidth={1.9} /></span>
      <span className={`text-[13.5px] font-semibold ${on ? "text-[#3E2818]" : "text-[#3E2818]/55"}`}>{x.name}</span>
    </button>
  )} />
);

function Rail({ render }) {
  return <div className="flex flex-col gap-1 max-w-xs">{SOLS.map((x, i) => render(x, i === ACTIVE, i))}</div>;
}

const OPTIONS = [
  { id: "a", title: "Option A · Solid Espresso Pill", note: "Active item fills with dark espresso, white text and a gold icon + arrow. Strong, confident, high-contrast.", comp: A },
  { id: "b", title: "Option B · Caramel Filled", note: "Active item fills with caramel brown and white text. Warm and brand-forward.", comp: B },
  { id: "c", title: "Option C · Minimal Gold Bar", note: "No fill — just a gold left bar, a tinted icon chip and bolder dark text. Clean and architectural.", comp: C },
  { id: "d", title: "Option D · Cream-Gold Ring Card", note: "Active item becomes a soft cream-gold card with a thin gold ring. Gentle and premium.", comp: D },
  { id: "e", title: "Option E · Filled Icon Chip", note: "Subtle white background; the icon chip fills caramel and the label turns caramel. Focus lands on the icon.", comp: E },
  { id: "f", title: "Option F · Elevated White + Gold Dot", note: "Active item lifts as a white card with a small gold status dot — no left bar. Modern and light.", comp: F },
];

export default function RailActiveOptions() {
  return (
    <div className="min-h-screen bg-[#EFE7D9]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Section 5 Left Rail · Active-State Options</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">Pick a design for the selected item</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Shown on the new Section 5 background. In each rail, &ldquo;Enterprise Brain&rdquo; is the selected item. Tell me the letter.</p>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
          {OPTIONS.map((o) => {
            const C = o.comp;
            return (
              <div key={o.id}>
                <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#B87A32] mb-3">{o.title}</p>
                <C />
                <p className="text-[12px] text-[#3E2818]/55 mt-3 leading-snug">{o.note}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
